import React, { useState } from 'react';
import {
	Control,
	FieldErrors,
	FieldValues,
	UseFormRegister,
	UseFormSetValue,
	UseFormTrigger,
	UseFormWatch,
} from 'react-hook-form';

import {
	Button,
	Confirm,
	Input,
	Label,
	Modal,
	RadioButton,
	Select,
	SpinnerOverlay,
	TextArea,
} from '@components/common';

import { useQueryPortalAvailableProjects } from '@hooks/portal/projects/portalProjects/useQueryPortalAvailableProjects';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { IPortalMilestoneDTO } from '@interfaces/portal/dto/milestonesManagementDTO';

import { MODAL_WIDTH_480 } from '@constants/modals';
import { MAX_80_LENGTH, MAX_1000_LENGTH } from '@constants/validationMessages';
import {
	fieldsToTestBasicInfo,
	milestoneFormFields,
	portalMilestoneStatuses,
	tabsNames,
} from '../constants';
import { changeProjectConfirmDescription, changeProjectConfirmText } from './constants';

import styles from './BasicInfo.module.scss';

interface IBasicInfoProps {
	isEditMode: boolean;
	isViewMode: boolean;
	register: UseFormRegister<FieldValues>;
	errors: FieldErrors;
	setValue: UseFormSetValue<FieldValues>;
	trigger: UseFormTrigger<FieldValues>;
	control: Control;
	isBasicInfoTabError: boolean;
	setCurrentTab: (value: string) => void;
	watch: UseFormWatch<FieldValues>;
	clearForm: () => void;
	isDirty: boolean;
	portalMilestone?: IPortalMilestoneDTO;
}

const BasicInfo = ({
	control,
	errors,
	register,
	setValue,
	isBasicInfoTabError,
	trigger,
	setCurrentTab,
	clearForm,
	isDirty,
	watch,
	isEditMode,
	isViewMode,
	portalMilestone,
}: IBasicInfoProps) => {
	const [isChangeProjectConfirm, setIsChangeProjectConfirm] = useState(false);
	const [projectToSelect, setProjectToSelect] = useState<TSelectOption<string>>(null);

	const descriptionValue = watch(milestoneFormFields.description);
	const statusValue = watch(milestoneFormFields.status);

	const closeChangeProjectConfirm = () => {
		setIsChangeProjectConfirm(false);
		setProjectToSelect(null);
	};

	const submitChangeProjectConfirm = () => {
		clearForm();

		setValue(milestoneFormFields.project, projectToSelect);

		setIsChangeProjectConfirm(false);
		setProjectToSelect(null);
	};

	const { portalAvailableProjects, isLoadingPortalAvailableProjects } = useQueryPortalAvailableProjects();

	const onSubmit = async () => {
		const isValid = await trigger(fieldsToTestBasicInfo);

		if (!isValid) return;

		setCurrentTab(tabsNames.details);
	};

	const onProjectChange = (option: TSelectOption<string>) => {
		if (!isDirty) {
			setValue(milestoneFormFields.project, option);

			return;
		}

		setIsChangeProjectConfirm(true);
		setProjectToSelect(option);
	};

	const onStatusChange = (option: string) => setValue(milestoneFormFields.status, option);

	const descriptionLength = descriptionValue?.trim().length || 0;
	const titleLength = watch(milestoneFormFields.title)?.trim().length || 0;

	return (
		<>
			<div className={styles.basicInfoContainer}>
				{isLoadingPortalAvailableProjects && <SpinnerOverlay />}
				{!isEditMode && !isViewMode
					&& <div className={styles.project}>
						<Select
							control={control}
							errorMessage={errors.project?.message}
							handleChange={onProjectChange}
							isError={!!errors?.project}
							label='Project'
							name={milestoneFormFields.project}
							options={portalAvailableProjects}
							placeholder='Choose project'
							required
						/>
					</div>
				}
				{(isEditMode || isViewMode) && portalMilestone
					&& <div className={styles.milestoneInfoContainer}>
						<div className={styles.milestoneInfo}>
							<div className={styles.milestoneInfoTitle}>Project:</div>
							<div className={styles.milestoneInfoValue}>{portalMilestone.projectName}</div>
						</div>
						<div className={styles.milestoneInfo}>
							<div className={styles.milestoneInfoTitle}>Milestone creator:</div>
							<div className={styles.milestoneInfoValue}>{portalMilestone.creator?.fullName || ''}</div>
						</div>
					</div>
				}
				{(isEditMode || isViewMode)
					&& <div className={styles.radioButtonsContainer}>
						<Label
							className={styles.labelText}
							label='Status'
							required
						/>
						<div className={styles.radioButtonContainer}>
							<RadioButton
								checked={statusValue === portalMilestoneStatuses.active}
								disabled={isViewMode}
								id={portalMilestoneStatuses.active}
								label='Active'
								name={milestoneFormFields.status}
								onChange={onStatusChange}
								register={register}
								value={portalMilestoneStatuses.active}
							/>
						</div>
						<div className={styles.radioButtonContainer}>
							<RadioButton
								checked={statusValue === portalMilestoneStatuses.completed}
								disabled={isViewMode}
								id={portalMilestoneStatuses.completed}
								label='Completed'
								name={milestoneFormFields.status}
								onChange={onStatusChange}
								register={register}
								value={portalMilestoneStatuses.completed}
							/>
						</div>
					</div>
				}
				<div className={styles.title}>
					<Input
						disabled={isViewMode}
						errorMessage={errors?.title?.message}
						isError={!!errors?.title}
						isMaxLengthCounter
						label='Title'
						maxLength={MAX_80_LENGTH}
						name={milestoneFormFields.title}
						placeholder='Enter title'
						register={register}
						required
						setValue={setValue}
						valueLength={titleLength}
					/>
				</div>
				<div className={styles.description}>
					<TextArea
						disabled={isViewMode}
						errorMessage={errors.description?.message}
						isMaxLengthCounter
						isNotResizable
						label='Milestone Description'
						maxLength={MAX_1000_LENGTH}
						name={milestoneFormFields.description}
						placeholder='Enter description'
						register={register}
						setValue={setValue}
						value={descriptionValue}
						valueLength={descriptionLength}
						withUseForms
					/>
				</div>
				{isChangeProjectConfirm && (
					<Modal
						isOpen={isChangeProjectConfirm}
						onClose={closeChangeProjectConfirm}
						title='Confirm action'
						width={MODAL_WIDTH_480}
					>
						<Confirm
							onClose={closeChangeProjectConfirm}
							onSubmit={submitChangeProjectConfirm}
							question={changeProjectConfirmText}
						>
							{changeProjectConfirmDescription}
						</Confirm>
					</Modal>
				)}
			</div>
			{!isEditMode && !isViewMode
				&& <div className={styles.submitButton}>
					<Button
						disabled={isBasicInfoTabError}
						onClick={onSubmit}
						text='Next'
					/>
				</div>
			}
		</>
	);
};

export default BasicInfo;
